import { formatLocaleTimeRangeOrEmpty } from 'app/shared/utils/format-locale-time';

/** See formatEventDate: a missing building timezone resolves to UTC, never the viewer's zone. */
const zoneOf = (timezone?: string): string => timezone || 'UTC';

const isRealDate = (value: string): boolean => Boolean(value) && !Number.isNaN(new Date(value).getTime());

/**
 * The event's duration, formatted entirely by `Intl.formatRange`.
 *
 * One call decides everything the old implementation decided by hand, and decides it per locale:
 *
 * - **whether to show a date at all.** This used to be `dayjs(start).isSame(dayjs(end), 'day')` with
 *   no timezone, so the decision was made in the VIEWER's zone while the dates either side were
 *   rendered in the building's — across midnight the two disagreed, differently for every viewer.
 *   `formatRange` compares the instants in the zone it is handed, so the decision and the output
 *   cannot diverge.
 * - **the separator.** Was `${start} - ${end}`, an ASCII hyphen no locale uses; CLDR wants an en dash,
 *   spaced per locale.
 * - **the shared day period.** `en` reads `5:00 – 5:15 PM`, not `5:00 PM - 5:15 PM`.
 * - **the dates on a cross-day range**, which it adds itself — `8/17/2026, 10:00 PM – 8/18/2026,
 *   2:00 AM`, and `17/08/2026 22 h 00 – 18/08/2026 02 h 00` in French. Hand-building that branch, as
 *   an earlier pass of this did, reintroduces exactly the locale-blind joins this exists to remove.
 */
export const formatEventDuration = (start_at: string, end_at: string, locale: string, tz: string): string => {
  // formatLocaleTimeRange throws RangeError on an invalid Date, where the previous toLocaleString
  // rendered the string "Invalid Date". Neither belongs on screen, and content is not required to
  // carry event times at all, so an unusable end yields an empty range rather than a crash.
  if (!isRealDate(start_at) || !isRealDate(end_at)) {
    return '';
  }

  return formatLocaleTimeRangeOrEmpty(new Date(start_at), new Date(end_at), locale, zoneOf(tz));
};
